<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useTableStore } from '@/store/storeTable'
import { useUserPreferencesStore } from "@/store/storeUserPreferences.js"
import BaseInput from "@/components/BaseComponents/BaseInput.vue"
import BaseProgressBar from "@/components/BaseComponents/BaseProgressBar.vue"

const userPreferencesStore = useUserPreferencesStore()
const { userPreferences } = storeToRefs(userPreferencesStore)

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true }
})

const emit = defineEmits(['update:modelValue'])
const hasError = ref(false)
const errorMessage = ref('')
const saving = ref(false)
const tableStore = useTableStore()
const formattedModelValue = ref('')

function formatPercentValue (value: number | string | null): string {
  if (value === null || value === '') return ''
  const numValue = Number(value) * 100
  const fraction = props.header.numberFormatDecimalPrecision ?? 2;
  const decimalSep = userPreferences.value.numberFormatDecimalSeparator ?? ','
  return `${numValue.toFixed(fraction).replace('.', decimalSep)}%`
}

function parsePercentValue (value: string): number | null {
  const cleanValue = value.replace('%', '').replace(',', '.').trim()
  if (!isNumeric(cleanValue)) return null
  return Number(cleanValue) / 100
}

onMounted(async () => {
  await userPreferencesStore.getUserPreferences()
  formattedModelValue.value = formatPercentValue(props.modelValue)
})

async function changed (newValue: string) {
  const parsedValue = parsePercentValue(newValue)

  if (parsedValue === null) {
    hasError.value = true
    errorMessage.value = 'Ugyldig prosentformat. Bruk tall med opptil to desimaler.'
    formattedModelValue.value = newValue
    return
  }

  emit('update:modelValue', parsedValue)

  if (!props.autoSave) return

  const item = {
    rowId: props.rowId,
    databaseObjectId: props.header.databaseObjectId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: parsedValue
  }

  hasError.value = false
  saving.value = true
  await tableStore.saveData(item)
  formattedModelValue.value = formatPercentValue(parsedValue)
  saving.value = false
}

function isNumeric (str: string): boolean {
  return !isNaN(str as any) && !isNaN(parseFloat(str))
}
</script>

<template>
  <div>
    <template v-if="readOnly">
      <div :class="{ 'right-align': singleLine }" class="verdi">
        <template v-if="modelValue != null && userPreferences">
          {{ formatPercentValue(modelValue) }}
        </template>
        <template v-else>
          <span class="verdi muted">(blank)</span>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="input-number-container">
        <BaseInput :error="hasError" :error-message="errorMessage" :model-value="formattedModelValue" type="text"
                   @change="changed"/>
      </div>
      <BaseProgressBar v-if="saving"/>
      <div v-else style="height: 4px;padding: 0;margin: 0;"/>
    </template>
  </div>
</template>

<style scoped>
.right-align {
  text-align: right;
  width: 100%;
}

.verdi {
  padding: 8px;
}

.verdi.muted {
  color: var(--gray-500);
  font-style: italic;
}

.input-number-container {
  width: 100%;
}
</style>
